/** Member photo avatar and photo-management menu for the member detail header. */
import { useEffect, useState, type MouseEvent } from 'react';
import { Avatar, Box, Menu, MenuItem } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  deleteMemberPhoto,
  fetchMemberPhotoBlob,
  toMemberPhotoErrorMessage,
} from '@/api/memberPhoto';
import { IconAction } from '@/components/IconAction';
import { useToast } from '@/components/useToast';
import { Icon, initialsOf, resolveTokens } from '@/theme';
import { MemberPhotoDialog } from './MemberPhotoDialog';

/**
 * Authed photo bytes → object URL (DNKADM-0304 U4). The serve endpoint
 * requires the bearer, so a plain `<img src>` 401s — the bytes come through
 * the authed client and render as a `blob:` URL (allowed by the Caddy
 * `img-src` policy since SASATH-0252). The object URL is revoked whenever the
 * inputs change or the component unmounts (a per-row leak on a long-lived
 * screen is a real cost). `reloadToken` keeps the pre-existing cache-bust
 * behaviour: an upload/remove bumps it, forcing a re-read. Fail-soft: any
 * failed read resolves to `null` and the initials avatar renders — never an
 * error state.
 */
function useMemberPhotoObjectUrl(photoUrl: string | null, reloadToken: number): string | null {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (photoUrl === null) {
      setSrc(null);
      return;
    }
    let cancelled = false;
    let created: string | null = null;
    void fetchMemberPhotoBlob(photoUrl, String(reloadToken)).then((blob) => {
      if (cancelled) return;
      if (blob === null) {
        setSrc(null);
        return;
      }
      created = URL.createObjectURL(blob);
      setSrc(created);
    });
    return () => {
      cancelled = true;
      if (created !== null) URL.revokeObjectURL(created);
      setSrc(null);
    };
  }, [photoUrl, reloadToken]);
  return src;
}

/**
 * The header avatar as the photo surface (DNKADM-0048): renders the contact's photo when
 * `photoUrl` is present (cache-busted), else the hashed-tint initials avatar. A camera
 * overlay opens Change / Remove. The member-detail screen is NOT `RequireAdmin`-gated
 * (`src/router.tsx` routes `members/:contactId` under Operations), so the photo endpoints
 * are the only authority: they enforce OWNER/ADMIN server-side and a refusal surfaces as
 * the ordinary error toast. DNKADM-0249 corrected the earlier claim that the screen was
 * admin-gated — the same class of unchecked assumption that produced DNKADM-0247's
 * permission trap.
 */
export function MemberPhotoAvatar({
  contactId,
  name,
  photoUrl,
  tint,
  reloadToken,
  photoEnabled = true,
  onReload,
}: {
  contactId: string;
  name: string;
  photoUrl: string | null;
  tint: { bg: string; fg: string };
  reloadToken: number;
  /**
   * Whether the member-photo surface exists for this session at all (DNKADM-0361).
   * `false` for the staff floor: `/v2/admin/members/{contactId}/photo` is `403` in
   * BOTH directions (HANDOFF §2), so the component renders the plain initials avatar
   * — no bytes fetched, no camera control, no dialog. Gating the READ as well as the
   * controls is deliberate: the read is fail-soft, so a `403` would degrade silently
   * to initials anyway, but issuing a request the server is known to refuse is the
   * thing this unit removes. Defaults to `true` — every existing caller is unchanged.
   */
  photoEnabled?: boolean;
  onReload: () => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const showToast = useToast();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [removing, setRemoving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const src = useMemberPhotoObjectUrl(photoEnabled ? photoUrl : null, reloadToken);
  const menuOpen = Boolean(anchorEl);

  function openMenu(e: MouseEvent<HTMLElement>) {
    setAnchorEl(e.currentTarget);
  }
  function closeMenu() {
    setAnchorEl(null);
  }
  function handleChange() {
    closeMenu();
    setDialogOpen(true);
  }
  async function handleRemove() {
    closeMenu();
    if (removing) return;
    setRemoving(true);
    try {
      await deleteMemberPhoto(contactId);
      showToast('Photo removed.');
      onReload();
    } catch (error) {
      showToast(toMemberPhotoErrorMessage(error), 'error');
    } finally {
      setRemoving(false);
    }
  }

  return (
    <Box sx={{ position: 'relative', width: 64, height: 64, flexShrink: 0 }}>
      <Avatar
        src={src ?? undefined}
        alt={src !== null ? `${name}’s photo` : undefined}
        sx={{ width: 64, height: 64, fontSize: 24, bgcolor: tint.bg, color: tint.fg }}
      >
        {initialsOf(name)}
      </Avatar>
      {photoEnabled && (
        <IconAction
          label="Change photo"
          aria-haspopup="menu"
          onClick={openMenu}
          disabled={removing}
          size="small"
          sx={{
            position: 'absolute',
            right: -4,
            bottom: -4,
            bgcolor: t.surface,
            border: `1px solid ${t.line}`,
            boxShadow: 1,
            '&:hover': { bgcolor: t.bg },
          }}
        >
          <Icon name="photo_camera" sx={{ fontSize: 17 }} />
        </IconAction>
      )}
      {photoEnabled && (
        <Menu anchorEl={anchorEl} open={menuOpen} onClose={closeMenu}>
          <MenuItem onClick={handleChange}>
            <Icon name="image" sx={{ fontSize: 18, mr: 1.25, color: t.text2 }} />
            Change photo
          </MenuItem>
          {photoUrl !== null && (
            <MenuItem onClick={handleRemove} disabled={removing}>
              <Icon name="delete" sx={{ fontSize: 18, mr: 1.25, color: t.text2 }} />
              Remove photo
            </MenuItem>
          )}
        </Menu>
      )}
      {photoEnabled && dialogOpen && (
        <MemberPhotoDialog
          contactId={contactId}
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onUploaded={() => {
            setDialogOpen(false);
            showToast('Photo updated.');
            onReload();
          }}
        />
      )}
    </Box>
  );
}
